/**
 * People-attribution S3 (plan 2026-08-29, decision D-F / AC-021): the ONE
 * wording for Meta-attributed purchases on every surface (campaign rows,
 * creative rows, drilldowns, saved views, traffic sources view).
 *
 * Like-for-like rule: the number that sits beside OUR purchases is Meta's
 * 7-day-click bucket — the served model's window, click-only. View-through
 * (1-day view) is a conversion class we cannot observe, so it is its own
 * labelled line and is never added into the comparable figure.
 *
 * Typed absence: a payload computed before S3 carries only the umbrella
 * `metaPurchases` (all windows). It is shown under its honest name «all
 * windows» — never relabelled as 7-day click.
 */
export interface MetaPurchaseBuckets {
  metaPurchases?: number;
  metaPurchases7dClick?: number;
  metaPurchasesViewThrough?: number;
}

export interface MetaPurchasesCopy {
  /** The comparable figure, e.g. «179 purchases · 7-day click». */
  primary: string;
  /** Short form for dense captions, e.g. «Meta 179 (7d click)». */
  compact: string;
  /** «+ 166 view-through» — present only when > 0; the «not observable» clause lives in the column guide / caption (S11 §14.2 wrap fix). */
  viewThrough?: string;
  /** Which bucket the primary figure is: 'seven_day_click' or 'all_windows' (legacy payload). */
  basis: 'seven_day_click' | 'all_windows';
}

const integer = (value: number): string => Math.round(value).toLocaleString('en-US');

export function metaPurchasesCopy(row: MetaPurchaseBuckets): MetaPurchasesCopy {
  if (typeof row.metaPurchases7dClick === 'number') {
    const viewThrough = typeof row.metaPurchasesViewThrough === 'number' && row.metaPurchasesViewThrough > 0
      ? `+ ${integer(row.metaPurchasesViewThrough)} view-through`
      : undefined;
    return {
      primary: `${integer(row.metaPurchases7dClick)} purchases · 7-day click`,
      compact: `Meta ${integer(row.metaPurchases7dClick)} (7d click)`,
      ...(viewThrough ? { viewThrough } : {}),
      basis: 'seven_day_click',
    };
  }
  const all = row.metaPurchases ?? 0;
  return {
    primary: `${integer(all)} purchases · all windows`,
    compact: `Meta ${integer(all)} (all windows)`,
    basis: 'all_windows',
  };
}
